/**
 * VoiceOrderingContainer Component
 * Voice-first ordering interface with centered microphone and popup ring
 */

import {
  Languages,
  Mic,
  MicOff,
  RotateCcw,
  Send,
  ShoppingCart,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { VOICE_VISUALIZATION_STYLE } from '../../../config/voiceVisualization'
import { useToast } from '../../../hooks/use-toast'
import { useAgenticChat } from '../../../hooks/useAgenticChat'
import { useVoiceRecording } from '../../../hooks/useVoiceRecording'
import { useWebRTCChat } from '../../../hooks/useWebRTCChat'
import { config } from '../../../lib/config'
import { useCartStore } from '../../../store/cartStore'
import { useSessionStore } from '../../../store/sessionStore'
import type {
  BotAction,
  ChatMessage,
  Order,
  Recommendation,
} from '../../../types/agenticChat'
import type { OrderItem } from '../../../types/cart'
import { playInterruptSound } from '../../../utils/soundEffects'
import { getWebRTCErrorMessage } from '../../../utils/webrtcErrors'
import { Button } from '../../ui/button'
import { Textarea } from '../../ui/textarea'
import { MicrophonePermission } from '../MicrophonePermission'
import { AIVisualization } from './AIVisualization'
import { MainRecommendations } from './MainRecommendations'
import { ModeSwitchButton } from './ModeSwitchButton'
import { OrderBottomSheet } from './OrderBottomSheet'
import { RecommendationDialog } from './RecommendationDialog'
import { getItemName } from './recommendationHelpers'
import { Recommendations } from './Recommendations'
import { RestaurantLogo } from './RestaurantLogo'
import { SessionChoiceModal } from './SessionChoiceModal'
import { ThemeSwitcher } from './ThemeSwitcher'
import { VoiceQuickActions } from './VoiceQuickActions'

interface VoiceOrderingContainerProps {
  restaurantId: string
  tableId: string
}

export type Language = 'en' | 'ar'

/**
 * Extract current order from messages (latest message with order action)
 */
function getCurrentOrderFromMessages(messages: ChatMessage[]): Order | null {
  // Find latest message with order action (including empty orders)
  const messagesWithOrders = messages.filter(
    (msg) =>
      msg.sender === 'ai' &&
      msg.actions?.some(
        (action) =>
          (action.action_type === 'update_order' ||
            action.action_type === 'order') &&
          action.order
      )
  )

  if (messagesWithOrders.length === 0) return null

  // Get latest message with order
  const latestOrderMessage = messagesWithOrders[messagesWithOrders.length - 1]
  const orderAction = latestOrderMessage.actions?.find(
    (action) =>
      (action.action_type === 'update_order' ||
        action.action_type === 'order') &&
      action.order
  )

  return orderAction?.order || null
}

/**
 * Convert AI order item to cart store format
 */
function convertAIOrderItemToCartItem(aiItem: {
  item_id: string
  item: string
  quantity: number
  price: number // in EGP
  comments?: string | null
}): OrderItem {
  const unitPricePiasters = Math.round(aiItem.price * 100) // Convert EGP to piasters
  const quantity = Math.max(0, aiItem.quantity) // Ensure non-negative

  // Extract name - AI might send bilingual names like "Arabic | English"
  const nameParts = aiItem.item.split('|').map(s => s.trim())
  const name = nameParts[1] || nameParts[0] || '' // English name or fallback
  const nameAr = nameParts[0] && nameParts.length > 1 ? nameParts[0] : '' // Arabic name if available

  return {
    id: aiItem.item_id, // Use item_id as both id and menuItemId for now
    menuItemId: aiItem.item_id,
    name,
    nameAr,
    quantity,
    unitPrice: unitPricePiasters,
    lineTotal: unitPricePiasters * quantity,
    specialInstructions: aiItem.comments || undefined,
  }
}

/**
 * Extract recommendations from actions
 */
function extractRecommendations(
  actions: BotAction[] | null | undefined
): Recommendation[] {
  if (!actions) return []

  const rawRecs =
    actions
      .filter((action) => action.action_type === 'recommendation')
      .flatMap((action) => action.recommendation || []) || []

  // Deduplicate by item name (case-insensitive, trim whitespace)
  // Also filter out any null/undefined items
  return rawRecs
    .filter((rec) => rec && rec.item) // Filter out null/undefined
    .filter(
      (rec, index, self) =>
        index ===
        self.findIndex(
          (r) => r.item.toLowerCase().trim() === rec.item.toLowerCase().trim()
        )
    )
}

export function VoiceOrderingContainer({
  restaurantId,
  tableId,
}: VoiceOrderingContainerProps) {
  const { i18n } = useTranslation()
  const language = (i18n.language === 'ar' ? 'ar' : 'en') as Language
  const [inputMode, setInputMode] = useState<'voice' | 'text'>('voice') // Start with STT interface
  const [textInput, setTextInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [selectedItem, setSelectedItem] = useState<Recommendation | null>(null)
  const [dismissedIndices, setDismissedIndices] = useState<Set<number>>(
    new Set()
  )
  const [isMuted, setIsMuted] = useState(false) // For AI audio output
  const [isListeningMuted, setIsListeningMuted] = useState(true) // Mic muted by default
  const [isOrderSheetOpen, setIsOrderSheetOpen] = useState(false)
  const audioPlayerRef = useRef<{ stop: () => void; play: (audioData: string, format: string, allowAutoplay?: boolean) => Promise<void> } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Toast notifications
  const { toast } = useToast()

  const {
    messages,
    error,
    sendUserMessage,
    sendAudioMessage,
    pushWebRTCMessage,
    pushUserMessage,
    isActive,
    isLoading,
    hasStoredSession,
    handleSessionChoice,
  } = useAgenticChat({
    restaurantId,
    tableId,
  })

  // WebRTC chat hook (feature-flagged)
  const {
    isConnected: isWebRTCConnected,
    connect: connectWebRTC,
    disconnect: disconnectWebRTC,
    sendMessage: sendWebRTCMessage,
    error: webrtcError,
  } = useWebRTCChat({
    restaurantId,
    tableId,
    onMessage: (data) => {
      // Handle incoming WebRTC messages by simulating agentic chat response logic
      // This is a bridge between WebRTC DataChannel and existing UI
      console.log('[VoiceOrderingContainer] WebRTC message received:', data);
      setIsSending(false); // Stop "Processing" state once data arrives
      pushWebRTCMessage(data);
    },
  })

  // Voice recording hook with automatic silence detection
  const {
    isRecording,
    startRecording,
    stopRecording,
    error: recordingError,
  } = useVoiceRecording({
    onSilenceDetected: async (audioData, format) => {
      // Automatically send audio when silence is detected
      if (config.useWebRTC) {
        if (isWebRTCConnected) {
          sendWebRTCMessage({ type: 'STOP_RECORDING' });
          setIsWebRTCRecording(false);
          setIsSending(true);
        } else {
          console.warn('[VoiceOrderingContainer] WebRTC not connected, silence ignored');
        }
      } else {
        try {
          await sendAudioMessage(audioData, format)
        } catch (err) {
          console.error(
            '[VoiceOrderingContainer] Failed to send audio after silence detection:',
            err
          )
          toast({
            variant: 'destructive',
            title: 'Error',
            description:
              err instanceof Error ? err.message : 'Failed to send audio. Please try again.',
          })
        }
      }
    },
  })

  const [lastConnectTime, setLastConnectTime] = useState(0);
  const CONNECT_COOLDOWN = 5000; // 5 seconds cooldown

  const [isWebRTCStarting, setIsWebRTCWebRTCStarting] = useState(false)

  // Auto-connect WebRTC if enabled - only on desktop or after first interaction
  useEffect(() => {
    if (!config.useWebRTC || !isActive || isWebRTCConnected || isWebRTCStarting) return;

    // On mobile, we wait for a manual start to satisfy autoplay policies
    // On desktop, we can try to auto-connect
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (!isMobile) {
      const now = Date.now();
      if (now - lastConnectTime > CONNECT_COOLDOWN) {
        setLastConnectTime(now);
        connectWebRTC();
      }
    }
  }, [isActive, isWebRTCConnected, isWebRTCStarting, connectWebRTC, lastConnectTime]);

  const handleManualStart = async () => {
    setIsWebRTCWebRTCStarting(true);
    // Prime the audio engine immediately on user gesture to satisfy Safari
    if (audioPlayerRef.current) {
      audioPlayerRef.current.play('UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA', 'wav', false).catch(() => {});
    }
    try {
      await connectWebRTC();
    } finally {
      setIsWebRTCWebRTCStarting(false);
    }
  };

  // Handle cleanup on unmount or when WebRTC should be off
  useEffect(() => {
    return () => {
      if (config.useWebRTC) disconnectWebRTC();
    };
  }, [disconnectWebRTC]);

  // Handle WebRTC errors
  useEffect(() => {
    if (webrtcError) {
      const errorInfo = getWebRTCErrorMessage(webrtcError, language);
      
      toast({
        variant: 'destructive',
        title: errorInfo.title,
        description: `${errorInfo.description} ${errorInfo.troubleshooting || ''}`,
      });

      console.error('[VoiceOrderingContainer] WebRTC error occurred:', webrtcError);
    }
  }, [webrtcError, toast, language]);

  // Get cart store actions
  const { setOrder, clearOrder } = useCartStore()
  const { orderId } = useSessionStore()

  // Compute processing state - disable interactivity when AI is processing
  const isProcessing = isSending || isLoading

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en')
  }

  const handleModeChange = (mode: 'voice' | 'text') => {
    setInputMode(mode)
    // Auto-focus text input when switching to text mode
    if (mode === 'text') {
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }

  const toggleMute = () => {
    setIsMuted((prev) => {
      const newMuted = !prev
      // Stop audio if muting and play interrupt sound
      if (newMuted && audioPlayerRef.current) {
        audioPlayerRef.current.stop()
        playInterruptSound()
      }
      return newMuted
    })
  }

  const toggleListeningMute = async () => {
    const newMuted = !isListeningMuted
    setIsListeningMuted(newMuted)
    
    // Control full voice experience: start/stop recording
    if (newMuted) {
      // Mute: Stop recording if active
      if (isCurrentlyRecording) {
        if (config.useWebRTC) {
          if (isWebRTCConnected) {
            sendWebRTCMessage({ type: 'STOP_RECORDING' });
            setIsWebRTCRecording(false);
          }
        } else {
          await handleStopRecording()
        }
      }
    } else {
      // Unmute: Start recording if in voice mode
      if (inputMode === 'voice' && !isCurrentlyRecording && !isProcessing) {
        if (config.useWebRTC) {
          if (isWebRTCConnected) {
            sendWebRTCMessage({ type: 'START_RECORDING' });
            setIsWebRTCRecording(true);
          } else {
             toast({
              variant: 'destructive',
              title: 'WebRTC Not Connected',
              description: 'Please wait for the connection to be established or check your network.',
            });
          }
        } else {
          await handleStartRecording()
        }
      }
    }
  }

  // Handle repeat button - replay last AI audio
  // This is user-initiated, so always try to play (pass allowAutoplay=false to bypass user interaction check)
  const handleRepeatAudio = async () => {
    if (latestAIMessage?.audio && audioPlayerRef.current && !isMuted) {
      try {
        // Stop any currently playing audio and play interrupt sound
        if (audioPlayerRef.current) {
          audioPlayerRef.current.stop()
          playInterruptSound()
        }
        // User-initiated action - always try to play (don't check user interaction)
        await audioPlayerRef.current.play(latestAIMessage.audio, latestAIMessage.audioFormat || 'wav', false)
      } catch (err) {
        console.error('[VoiceOrderingContainer] Failed to repeat audio:', err)
      }
    }
  }

  const [isWebRTCRecording, setIsWebRTCRecording] = useState(false)

  // Combined recording state for UI
  const isCurrentlyRecording = config.useWebRTC ? isWebRTCRecording : isRecording;

  // Handle start recording
  const handleStartRecording = async () => {
    // Check if HTTPS is required for mic access
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      const errorInfo = getWebRTCErrorMessage('HTTPS_REQUIRED', language);
      toast({
        variant: 'destructive',
        title: errorInfo.title,
        description: `${errorInfo.description} ${errorInfo.troubleshooting || ''}`,
      });
      return;
    }

    try {
      if (config.useWebRTC && isWebRTCConnected) {
        // In WebRTC mode, we just send the command. The stream is already active.
        sendWebRTCMessage({ type: 'START_RECORDING' });
        setIsWebRTCRecording(true);
        // Reset isSending just in case we were stuck
        setIsSending(false);
      } else {
        await startRecording()
      }
    } catch (err) {
      console.error('[VoiceOrderingContainer] Failed to start recording:', err)
      toast({
        variant: 'destructive',
        title: 'Recording Error',
        description: err instanceof Error ? err.message : 'Failed to start recording. Please try again.',
      })
    }
  }

  // Handle stop recording and send audio (manual stop - user clicks mic again)
  const handleStopRecording = async () => {
    try {
      if (config.useWebRTC && isWebRTCConnected) {
        // In WebRTC mode, we just send the command.
        sendWebRTCMessage({ type: 'STOP_RECORDING' });
        setIsWebRTCRecording(false);
        setIsSending(true); // Trigger processing state in UI
        
        // Safety timeout: If AI doesn't respond in 15s, unlock the UI
        setTimeout(() => setIsSending(false), 15000);

      } else {
        const result = await stopRecording()
        if (result) {
          await sendAudioMessage(result.audioData, result.format)
        }
      }
    } catch (err) {
      console.error('[VoiceOrderingContainer] Failed to stop recording or send audio:', err)
      setIsSending(false);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to send audio. Please try again.',
      })
    }
  }

  // Handle recording errors
  useEffect(() => {
    if (recordingError) {
      toast({
        variant: 'destructive',
        title: 'Recording Error',
        description: recordingError,
      })
    }
  }, [recordingError, toast])

  // Handle text input submission
  const handleTextSubmit = async () => {
    if (!textInput.trim() || isProcessing || !isActive) return

    const message = textInput.trim()
    setTextInput('')
    setIsSending(true)

    try {
      if (config.useWebRTC && isWebRTCConnected) {
        sendWebRTCMessage({ type: 'TXT', text: message });
        pushUserMessage(message);
      } else {
        await sendUserMessage(message)
      }
    } catch (err) {
      console.error('[VoiceOrderingContainer] Failed to send message:', err)
    } finally {
      setIsSending(false)
      // Refocus textarea after sending
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }

  // Handle Enter key (submit) or Shift+Enter (new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleTextSubmit()
    }
  }

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [textInput])

  // Get latest AI message for audio playback and recommendations
  const latestAIMessage = messages
    .slice()
    .reverse()
    .find((msg) => msg.sender === 'ai')

  // Recommendations state - persist until AI changes them (null doesn't count as change)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])

  // Update recommendations only when they actually change (not when null/empty)
  useEffect(() => {
    if (!latestAIMessage?.actions) return

    const newRecommendations = extractRecommendations(latestAIMessage.actions)

    // Only update if new recommendations exist and are different
    if (newRecommendations.length > 0) {
      // Compare with current recommendations
      const currentIds = recommendations
        .map((r) => r.item.toLowerCase().trim())
        .sort()
        .join(',')
      const newIds = newRecommendations
        .map((r) => r.item.toLowerCase().trim())
        .sort()
        .join(',')

      if (currentIds !== newIds) {
        setRecommendations(newRecommendations)
      }
    }
    // If newRecommendations is empty/null, don't update (keep existing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestAIMessage?.id, latestAIMessage?.actions])

  // Derive current order from AI messages
  const currentOrder = useMemo(
    () => getCurrentOrderFromMessages(messages),
    [messages]
  )

  // Sync cart store with AI's currentOrder
  // Always update UI based on AI's order object - if no order object or empty items, clear the cart
  useEffect(() => {
    // If no order object from AI, clear the cart store
    if (!currentOrder || !currentOrder.items || currentOrder.items.length === 0) {
      if (orderId) {
        clearOrder()
      }
      return
    }

    if (!orderId) return

    // Filter out items with quantity <= 0 (defensive check)
    const validItems = currentOrder.items.filter(item => item.quantity > 0)
    
    // If no valid items after filtering, clear the cart
    if (validItems.length === 0) {
      clearOrder()
      return
    }
    
    // Convert AI order items to cart store format
    const cartItems: OrderItem[] = validItems.map(convertAIOrderItemToCartItem)
    
    // Calculate total from items (convert EGP to piasters)
    const totalPiasters = currentOrder.total_price
      ? Math.round(currentOrder.total_price * 100)
      : cartItems.reduce((sum, item) => sum + item.lineTotal, 0)

    // Update cart store with AI's order
    setOrder({
      orderId,
      items: cartItems,
      total: totalPiasters,
      status: currentOrder.status === 'submitted' ? 'submitted' : 
              currentOrder.status === 'pending' ? 'pending' : 'draft',
    })
  }, [currentOrder, orderId, setOrder, clearOrder])

  // Calculate order item count
  const orderItemCount =
    currentOrder?.items?.filter(item => item.quantity > 0).reduce((sum, item) => sum + item.quantity, 0) || 0

  // Reset dismissed indices when new recommendations arrive
  useEffect(() => {
    if (recommendations.length > 0) {
      setDismissedIndices(new Set())
    }
  }, [recommendations.length])

  // Handle recommendation circle click - open dialog
  const handleRecommendationClick = (index: number) => {
    setSelectedItem(recommendations[index])
  }

  // Handle add to order from dialog - notify AI only (no local state management)
  const handleAddToOrder = async (item: Recommendation, quantity: number) => {
    const itemName = getItemName(item.item, language)

    setSelectedItem(null) // Close dialog

    // Notify AI about the addition
    if (isActive) {
      const message =
        language === 'en'
          ? `I added ${quantity}x ${itemName}`
          : `أضفت ${quantity}× ${itemName}`
      try {
        await sendUserMessage(message)
      } catch (err) {
        console.error(
          '[VoiceOrderingContainer] Failed to notify AI about item addition:',
          err
        )
        // Don't block UI - just log error
      }
    }
  }

  // Handle quantity update notification to AI
  const handleQuantityUpdateNotification = async (
    itemName: string,
    newQuantity: number
  ) => {
    if (!isActive) return

    const message =
      language === 'en'
        ? `I updated ${itemName} quantity to ${newQuantity}`
        : `حدثت كمية ${itemName} إلى ${newQuantity}`

    try {
      await sendUserMessage(message)
    } catch (err) {
      console.error(
        '[VoiceOrderingContainer] Failed to notify AI about quantity update:',
        err
      )
    }
  }

  // Handle item removal notification to AI
  const handleItemRemoveNotification = async (itemName: string) => {
    if (!isActive) return

    const message =
      language === 'en'
        ? `I removed ${itemName} from my order`
        : `أزلت ${itemName} من طلبي`

    try {
      await sendUserMessage(message)
    } catch (err) {
      console.error(
        '[VoiceOrderingContainer] Failed to notify AI about item removal:',
        err
      )
    }
  }

  // Handle quick action button click
  const handleQuickActionClick = async (message: string) => {
    if (isProcessing || !isActive) return

    setIsSending(true)
    try {
      await sendUserMessage(message)
    } catch (err) {
      console.error(
        '[VoiceOrderingContainer] Failed to send quick action message:',
        err
      )
    } finally {
      setIsSending(false)
    }
  }

  // Split recommendations into main (first 2) and carousel (rest)
  const mainRecommendations = recommendations.slice(0, 2)
  const remainingRecommendations = recommendations.slice(2)

  // Check if user has sent any messages - logo should hide once user responds
  const hasUserMessages = messages.some((msg) => msg.sender === 'customer')

  return (
    <div
      className="flex h-screen flex-col"
      style={{
        background: 'var(--background)', // Slate Refinement background - adapts to dark mode
        direction: language === 'ar' ? 'rtl' : 'ltr',
      }}
    >
      {/* Header - Compact */}
      <header
        className="border-b px-4 py-3 text-center relative z-30"
        style={{ borderColor: 'var(--border)', background: 'var(--background)' }}
      >
        {/* Language Toggle - Compact Design */}
        <button
          onClick={toggleLanguage}
          className="absolute left-4 top-3 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-all hover:opacity-80"
          style={{
            background: 'var(--accent-gold)',
            color: 'white',
          }}
          aria-label="Toggle language"
        >
          <Languages className="h-3.5 w-3.5" />
          <span>{language === 'en' ? 'AR' : 'EN'}</span>
        </button>

        {/* Theme Switcher - Header (Compact) */}
        <div className="absolute right-4 top-3">
          <ThemeSwitcher size="compact" />
        </div>

        {/* Mute Button - Top Right (Hidden for now, will be needed for full speech-to-text interaction) */}
        {false && (
          <button
            onClick={toggleMute}
            className="absolute right-4 top-3 flex items-center justify-center rounded-lg px-2 py-1.5 transition-all hover:opacity-80"
            style={{
              background: isMuted ? 'var(--error-light)' : 'var(--accent-gold)',
              color: 'white',
              minWidth: '44px',
              minHeight: '44px',
            }}
            aria-label={
              language === 'en'
                ? isMuted
                  ? 'Unmute audio'
                  : 'Mute audio'
                : isMuted
                  ? 'إلغاء كتم الصوت'
                  : 'كتم الصوت'
            }
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </button>
        )}

        <h1
          className="text-lg font-semibold"
          style={{ color: 'var(--text-primary)' }}
        >
          WaiterGPT
        </h1>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          { 'Your Personal Digital Waiter'}
        </p>
      </header>

      {/* Main Content Area - Recommendations and Controls */}
      <div
        className="relative flex flex-1 flex-col overflow-y-auto"
        style={{ paddingBottom: '100px' }}
      >
        {/* Restaurant Logo - Show until user sends their first message */}
        {!hasUserMessages && <RestaurantLogo />}

        {/* Start Session Overlay for WebRTC Mobile */}
        {config.useWebRTC && !isWebRTCConnected && (
          <div className="absolute inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 p-6 text-center">
              <h2 className="text-xl font-bold">Ready to order?</h2>
              <p className="text-sm text-muted-foreground">
                Tap the button below to start your AI voice session.
              </p>
              <Button 
                onClick={handleManualStart} 
                disabled={isWebRTCStarting}
                className="h-16 w-16 rounded-full bg-gold shadow-lg"
              >
                {isWebRTCStarting ? (
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Mic className="h-8 w-8 text-white" />
                )}
              </Button>
              <span className="text-xs font-medium text-gold animate-pulse">
                {isWebRTCStarting ? 'Connecting...' : 'Tap to Start'}
              </span>
            </div>
          </div>
        )}

        {/* Microphone Permission Handling - Show when voice mode active and blocked */}
        {inputMode === 'voice' && (
          <MicrophonePermission 
            language={language} 
            onPermissionDenied={() => setInputMode('text')}
          />
        )}

        {/* Main Recommendations - First 2 as large cards */}
        {mainRecommendations.length > 0 && (
          <div className="pt-4 pb-1">
            {/* Top Recommendations Label */}
            <h3
              className="text-sm font-semibold px-4 mb-2"
              style={{
                color: 'var(--text-secondary)',
                textAlign: language === 'ar' ? 'right' : 'left',
              }}
            >
              {language === 'en' ? 'Top Recommendations' : 'التوصيات المميزة'}
            </h3>
            <MainRecommendations
              recommendations={mainRecommendations}
              onItemClick={(item) => setSelectedItem(item)}
              onAddToOrder={(item) => handleAddToOrder(item, 1)}
              language={language}
              dismissedIndices={dismissedIndices}
              disabled={isProcessing}
            />
          </div>
        )}

        {/* Carousel Recommendations - Remaining items as circles with titles */}
        {remainingRecommendations.length > 0 && (
          <div className="pb-1">
            {/* Other Recommendations Label */}
            <h3
              className="text-sm font-semibold px-4 mb-2"
              style={{
                color: 'var(--text-secondary)',
                textAlign: language === 'ar' ? 'right' : 'left',
              }}
            >
              {language === 'en' ? 'Other Options' : 'خيارات أخرى'}
            </h3>
            <Recommendations
              recommendations={recommendations}
              onItemClick={handleRecommendationClick}
              dismissedIndices={dismissedIndices}
              language={language}
              skipFirst={2}
              disabled={isProcessing}
            />
          </div>
        )}

        {/* Spacer to push input area to bottom */}
        <div className="flex-1" />

        {/* Error Message */}
        {error && (
          <div
            className="absolute top-20 left-1/2 -translate-x-1/2 rounded-lg px-4 py-2 text-sm shadow-lg z-50"
            style={{
              background: 'var(--alert-destructive-bg)',
              color: 'var(--error)',
              border: '1px solid var(--error-light)',
            }}
          >
            {error}
          </div>
        )}

        {/* Session Ended */}
        {!isActive && (
          <div
            className="absolute top-20 left-1/2 -translate-x-1/2 rounded-lg px-4 py-3 text-center text-sm shadow-lg z-50"
            style={{
              background: 'var(--card-bg)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
            }}
          >
            {language === 'en'
              ? 'Session ended. Scan a new QR code to start ordering.'
              : 'انتهت الجلسة. امسح رمز QR جديد لبدء الطلب.'}
          </div>
        )}
      </div>

      {/* Input Area Container - Fixed at bottom with flexbox layout */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 flex flex-col"
        style={{ background: 'var(--background)', paddingBottom: '8px' }}
      >
        {/* Full-width border line - like header */}
        <div 
          className="w-full"
          style={{ borderBottom: '1px solid var(--border)' }}
        />
        
        {/* Voice Mode: Bottom-up layout - Visualization with buttons, divider, then quick actions + mode switch */}
        {inputMode === 'voice' && (
          <div className="flex flex-col">
            {/* Visualization Row: Mute (left) + Space + Visualization (center) + Space + Repeat (right) */}
            <div className="flex items-center justify-between w-full px-4 py-3">
              {/* Left: Mute/Unmute Listening Button */}
              <button
                onClick={toggleListeningMute}
                disabled={!isActive || (isProcessing && !isCurrentlyRecording)}
                className="flex-shrink-0 rounded-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  background: isListeningMuted
                    ? 'var(--error-light)'
                    : isCurrentlyRecording
                    ? 'var(--accent-gold)'
                    : 'var(--card-bg)',
                  color: isListeningMuted || isCurrentlyRecording ? 'white' : 'var(--text-primary)',
                  border: `1.5px solid ${isListeningMuted ? 'var(--error-light)' : isCurrentlyRecording ? 'var(--accent-gold)' : 'var(--border)'}`,
                  boxShadow: isCurrentlyRecording ? '0 4px 12px rgba(168, 146, 107, 0.4)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                  animation: isCurrentlyRecording ? 'pulseVoice 1.5s infinite' : 'none',
                }}
                aria-label={
                  language === 'en'
                    ? isListeningMuted
                      ? 'Unmute - Start voice recording'
                      : isCurrentlyRecording
                      ? 'Mute - Stop voice recording'
                      : 'Start voice recording'
                    : isListeningMuted
                      ? 'إلغاء الكتم - بدء تسجيل الصوت'
                      : isCurrentlyRecording
                      ? 'كتم - إيقاف تسجيل الصوت'
                      : 'بدء تسجيل الصوت'
                }
                title={
                  language === 'en'
                    ? isListeningMuted
                      ? 'Unmute - Start voice recording'
                      : isCurrentlyRecording
                      ? 'Mute - Stop voice recording'
                      : 'Start voice recording'
                    : isListeningMuted
                      ? 'إلغاء الكتم - بدء تسجيل الصوت'
                      : isCurrentlyRecording
                      ? 'كتم - إيقاف تسجيل الصوت'
                      : 'بدء تسجيل الصوت'
                }
              >
                {isListeningMuted ? (
                  <MicOff className="h-6 w-6" />
                ) : (
                  <Mic className="h-6 w-6" />
                )}
              </button>

              {/* Center: Visualization */}
              <div className="flex-1 flex items-center justify-center">
                <AIVisualization
                  onSendMessage={sendUserMessage}
                  isPlaying={!!latestAIMessage?.audio}
                  audioData={latestAIMessage?.audio || null}
                  audioFormat={latestAIMessage?.audioFormat || 'wav'}
                  disabled={true}
                  size="xsmall"
                  isMuted={isMuted}
                  isRecording={isCurrentlyRecording}
                  recordingMode={inputMode === 'voice'}
                  isLoading={isLoading}
                  visualizationStyle={VOICE_VISUALIZATION_STYLE}
                  onAudioPlayerReady={(player) => {
                    audioPlayerRef.current = player
                  }}
                />
              </div>

              {/* Right: Repeat Audio Button */}
              <button
                onClick={handleRepeatAudio}
                disabled={!isActive || isProcessing || !latestAIMessage?.audio || isMuted}
                className="flex-shrink-0 rounded-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  background: 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  border: '1.5px solid var(--border)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
                aria-label={
                  language === 'en'
                    ? 'Repeat last AI message'
                    : 'تكرار آخر رسالة من الذكاء الاصطناعي'
                }
                title={
                  language === 'en'
                    ? 'Repeat - Play last AI message again'
                    : 'تكرار - تشغيل آخر رسالة من الذكاء الاصطناعي مرة أخرى'
                }
              >
                <RotateCcw className="h-6 w-6" />
              </button>
            </div>

            {/* Divider */}
            <div 
              className="w-full"
              style={{ borderTop: '1px solid var(--border)' }}
            />

            {/* Bottom Row: Quick Actions (left) + Mode Switch (right) */}
            <div className="flex items-center justify-between px-4 py-2">
              {/* Left: Quick Action Icons */}
              <div className="flex-shrink-0">
                <VoiceQuickActions
                  onActionClick={handleQuickActionClick}
                  language={language}
                  disabled={!isActive || isProcessing}
                />
              </div>

              {/* Right: Mode Switch Button */}
              <div className="flex-shrink-0">
                <ModeSwitchButton
                  mode={inputMode}
                  onModeChange={handleModeChange}
                  language={language}
                  disabled={!isActive || isProcessing}
                />
              </div>
            </div>
          </div>
        )}

        {/* Text Mode: Two-row layout - Top: Quick Actions (left) + Mode Switch (right), Bottom: Text Input */}
        {inputMode === 'text' && (
          <div className="flex flex-col gap-3 px-4 pt-3 pb-2">
            {/* Top Row: Quick Actions (left) + Mode Switch (right) */}
            <div className="flex items-center justify-between">
              {/* Left: Quick Action Icons */}
              <div className="flex-shrink-0">
                <VoiceQuickActions
                  onActionClick={handleQuickActionClick}
                  language={language}
                  disabled={!isActive || isProcessing}
                />
              </div>

              {/* Right: Mode Switch Button */}
              <div className="flex-shrink-0">
                <ModeSwitchButton
                  mode={inputMode}
                  onModeChange={handleModeChange}
                  language={language}
                  disabled={!isActive || isProcessing}
                />
              </div>
            </div>

            {/* Bottom Row: Text Input Container */}
            <div
              className="w-full max-w-lg mx-auto transition-all"
              style={{
                direction: language === 'ar' ? 'rtl' : 'ltr',
              }}
            >
              <div
                className="flex gap-1.5 rounded-lg border py-1.5 px-2 shadow-lg"
                style={{
                  background: 'var(--card-bg)',
                  borderColor: 'var(--accent-gold)',
                  borderWidth: '2px',
                }}
              >
                <Textarea
                  ref={textareaRef}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    language === 'en'
                      ? 'Type your message...'
                      : 'اكتب رسالتك... (اضغط Enter للإرسال)'
                  }
                  disabled={!isActive || isProcessing}
                  rows={1}
                  className="min-h-[40px] max-h-[120px] resize-none px-2 py-1.5 text-sm"
                  style={{
                    background: 'var(--background)',
                    color: 'var(--text-primary)',
                  }}
                />
                <Button
                  onClick={handleTextSubmit}
                  disabled={!textInput.trim() || isProcessing || !isActive}
                  className="flex-shrink-0 px-3 rounded-full"
                  style={{
                    background: 'var(--accent-gold)',
                    color: 'white',
                    minHeight: '40px',
                  }}
                  aria-label={
                    language === 'en' ? 'Send message' : 'إرسال الرسالة'
                  }
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Hidden AI visualization for audio playback in text mode */}
        {inputMode === 'text' && (
          <div style={{ display: 'none' }}>
            <AIVisualization
              onSendMessage={sendUserMessage}
              isPlaying={!!latestAIMessage?.audio}
              audioData={latestAIMessage?.audio || null}
              audioFormat={latestAIMessage?.audioFormat || 'wav'}
              disabled={!isActive || isProcessing}
              size="small"
              isMuted={isMuted}
              isLoading={isLoading}
              visualizationStyle={VOICE_VISUALIZATION_STYLE}
              onAudioPlayerReady={(player) => {
                audioPlayerRef.current = player
              }}
            />
          </div>
        )}
      </div>

      {/* Session Choice Modal */}
      <SessionChoiceModal
        isOpen={hasStoredSession}
        language={language}
        onContinue={() => handleSessionChoice('continue')}
        onStartNew={() => {
          handleSessionChoice('new')
        }}
      />

      {/* Recommendation Dialog */}
      <RecommendationDialog
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onAddToOrder={handleAddToOrder}
        language={language}
      />

      {/* Order Bottom Sheet */}
      <OrderBottomSheet
        isOpen={isOrderSheetOpen}
        onClose={() => setIsOrderSheetOpen(false)}
        language={language}
        items={currentOrder?.items || []}
        total={
          currentOrder?.total_price ?? 
          (currentOrder?.items?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0)
        }
        onQuantityUpdate={handleQuantityUpdateNotification}
        onItemRemove={handleItemRemoveNotification}
      />

      {/* Order Icon Button with Badge - Fixed Position */}
      {orderItemCount > 0 && (
        <button
          onClick={() => setIsOrderSheetOpen(true)}
          className="fixed bottom-43 right-4 z-50 flex items-center justify-center rounded-full shadow-lg transition-all hover:scale-105 active:scale-95"
          style={{
            width: '48px',
            height: '48px',
            background: 'var(--accent-gold)',
            color: 'white',
          }}
          aria-label={language === 'en' ? 'View order' : 'عرض الطلب'}
        >
          <ShoppingCart className="h-5 w-5" />
          {/* Badge */}
          <div
            className="absolute -top-1 -right-1 flex items-center justify-center rounded-full text-xs font-bold"
            style={{
              minWidth: '18px',
              height: '18px',
              padding: '0 4px',
              background: 'var(--error-light)',
              color: 'white',
            }}
          >
            {orderItemCount > 99 ? '99+' : orderItemCount}
          </div>
        </button>
      )}

      {/* Order Summary Bar - Disabled for now */}
      {/* {!isCartEmpty() && (
        <OrderSummaryBar 
          language={language}
          isExpanded={isOrderBarExpanded}
          onExpandedChange={setIsOrderBarExpanded}
        />
      )} */}

      {/* CSS Animations */}
      <style>{`
        @keyframes pulseVoice {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 12px rgba(168, 146, 107, 0.4);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 6px 16px rgba(168, 146, 107, 0.6);
          }
        }
      `}</style>
    </div>
  )
}
